/**
 * 機密バッファをゼロ埋めする best-effort ヘルパ。
 *
 * ⚠️ 制限事項:
 *   - JavaScript の `string` は immutable のため zeroize **不可**
 *     (ガベージコレクト前に複製がメモリに残る可能性あり)
 *   - String 値 (Master Password 等) を Uint8Array に encode 後、
 *     即 utf8 文字列をスコープ外に逃がして以降は Buffer のみで扱う運用が必須
 *   - `crypto.subtle.importKey({ extractable: false })` で派生鍵は extractable=false にして
 *     JavaScript ヒープに鍵 bytes を残さない設計を併用すること
 *
 * 詳細運用ルール: apps/api/src/crypto/KEY_HIERARCHY.md §6 「鍵の生存時間」
 */

/** 単一の Uint8Array をゼロ埋め (in-place)。null/undefined は no-op。 */
export const zeroize = (buf: Uint8Array | null | undefined): void => {
  if (!buf) return
  buf.fill(0)
}

/** 可変個の Uint8Array をまとめてゼロ埋め。順序は左から右。 */
export const zeroizeAll = (...bufs: Array<Uint8Array | null | undefined>): void => {
  for (const b of bufs) zeroize(b)
}

/**
 * 機密バッファを使う try/finally の補助。
 * `using` 構文 (TC39 explicit resource management) のシム。
 *
 * ```ts
 * await withZeroize([mk, smkEnc], async () => {
 *   // 鍵を使う処理
 * });
 * // ↑ 戻ったタイミングで自動 zeroize
 * ```
 */
export const withZeroize = async <T>(bufs: Uint8Array[], fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn()
  } finally {
    zeroizeAll(...bufs)
  }
}
